import { appService, initAuth } from '@ibe/components';
import { ConfigService, defaultConfig, LoggerFactory } from '@ibe/services';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import dayjs from 'dayjs';
import { fr } from 'date-fns/locale/fr';
import { nl } from 'date-fns/locale/nl';
import localeData from 'dayjs/plugin/localeData';
import localeFr from 'dayjs/locale/fr';
import localeNl from 'dayjs/locale/nl';
import { initTracking } from '@/Tracking/setup';
import '../Tracking/trackingSubscriptions';
import isClient from '@/Util/globals';
import { MGNLConfigType, PageProps } from '@/Util/magnolia';
import YsnConfigModel from '@/Config/YsnConfigModel';
import { ApiInventory } from '@ibe/api';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import {
  ConfigUrlParams,
  Currency,
  currency,
  fallbackLng,
  getOptionsClient,
  Language,
  languages
} from '@/app/i18n/settingsClient';

dayjs.extend(localeData);

export const productSearchSessionKey = 'ysn-product-search-response';

export const getLocale = (lang: string) => {
  switch (lang) {
    case Language.NL:
      return localeNl;
    case Language.FR:
      return localeFr;
    default:
      return localeNl;
  }
};

const logger = LoggerFactory.get('config');
const customConfig: Partial<YsnConfigModel> = {
  defaultLanguage: 'nl',
  defaultCurrency: 'EUR',
  apiUrl: '/api/',
  configUrl: '/config.json',
  affiliateAgencyNumber: 0,
  displayFormatDate: {
    de_DE: 'DD.MM.YYYY',
    en_GB: 'DD/MM/YYYY',
    fr_FR: 'DD/MM/YYYY',
    it_IT: 'DD/MM/YYYY',
    de: 'DD.MM.YYYY',
    en: 'DD/MM/YYYY',
    nl: 'DD/MM/YYYY',
    it: 'DD/MM/YYYY',
    fr: 'DD/MM/YYYY',
    cs: 'DD/MM/YYYY'
  },
  displayFormatDateTime: {
    de_DE: 'DD.MM.YYYY HH:mm',
    en_GB: 'DD/MM/YYYY HH:mm',
    fr_FR: 'DD/MM/YYYY HH:mm',
    it_IT: 'DD/MM/YYYY HH:mm',
    de: 'DD.MM.YYYY HH:mm',
    en: 'DD/MM/YYYY HH:mm',
    nl: 'DD/MM/YYYY HH:mm',
    fr: 'DD/MM/YYYY HH:mm',
    it: 'DD/MM/YYYY HH:mm',
    cs: 'DD/MM/YYYY HH:mm'
  },
  displayFormatDateShort: {
    de_DE: 'DD.MM.',
    en_GB: 'DD/MM',
    fr_FR: 'DD/MM',
    it_IT: 'DD/MM',
    de: 'DD.MM.',
    en: 'DD/MM',
    nl: 'DD/MM',
    it: 'DD/MM',
    fr: 'DD/MM',
    cs: 'DD/MM'
  },
  displayFormatWeekDay: {
    de_DE: 'ddd DD.MM.',
    en_GB: 'ddd DD/MM',
    fr_FR: 'ddd DD/MM',
    it_IT: 'ddd DD/MM',
    de: 'ddd DD.MM.',
    en: 'ddd DD/MM',
    fr: 'ddd DD/MM',
    it: 'ddd DD/MM',
    cs: 'ddd DD/MM'
  },
  useConsentManager: true,
  googleTagManagerContainerId: 'GTM-TMBVKV6',
  datePickerMinDateOffset: 0,
  session: {
    expirationTimeNotificationOffset: 5
  },
  sessionKeyTabsShared: true,
  traveler: {
    maxChildAge: 12,
    maxInfantAge: 2,
    mockAvailable: true,
    genderValues: []
  },
  useIndexedGeoUnits: true,
  enableMultipleRooms: true,
  maxDaysInDayRangeMultiRoomSearch: 31,
  stickySearchOffsetClass: 'top-header--mobile',
  outsideElementsContainerId: 'iso',
  paxRoomConfig: {
    maxRooms: 4,
    maxCapacity: 64,
    maxAdults: 9,
    maxChildren: 8
  },
  internFlightInventories: [ApiInventory.TOP],
  frontendCache: {
    ttl: 5, // in Minute
    enable: true,
    filteredResultsMaxSize: 20
  },
  filterSortOption: {
    MultipleSelectionFilter: {
      PACKAGE_CATEGORY: 'VALUE'
    }
  },
  filterSortOptionOrder: {
    MultipleSelectionFilter: {
      PACKAGE_CATEGORY: 'DESC'
    }
  }
};

const durationOptions = {
  WEEKS: [1, 2, 3, 4],
  NIGHTS: [...Array.from({ length: 21 }, (_, i) => i + 1).filter(i => !!(i % 7 && i > 2))]
};

defaultConfig.packageSearchDurationOptions = durationOptions;

export const configService = new ConfigService<YsnConfigModel>(YsnConfigModel, defaultConfig);

function initLanguage(locale?: string): void {
  registerLocale('fr', fr);
  registerLocale('nl', nl);
  appService.setLang(locale ?? fallbackLng);
  dayjs.locale(getLocale(locale ?? fallbackLng));
  setDefaultLocale(locale ?? fallbackLng);
}

function initCurrency(): void {
  if (!isClient()) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  let currencyFromUrl: string | null = null;
  if (params.has(ConfigUrlParams.CURRENCY)) {
    currencyFromUrl = params.get(ConfigUrlParams.CURRENCY);
    if (currency.findIndex(cur => cur === currencyFromUrl) === -1) {
      console.warn(`Not supported currency passed via URL: ${currencyFromUrl}`);
      currencyFromUrl = null;
    }
  }
  if (appService.currency === '' && currencyFromUrl === null) {
    appService.setCurrency(Currency.EUR);
  }
}

export function initConfig(params: MGNLConfigType | undefined) {
  let externalConfig = {
    apiUrl: process.env.NEXT_PUBLIC_API_URL
  } as Partial<YsnConfigModel>;
  if (params?.['@path']) {
    externalConfig = {
      ...externalConfig,
      ...(params as Partial<YsnConfigModel>)
    };
  }
  configService.mergeIntoConfig(customConfig);
  configService.mergeIntoConfig(externalConfig);
}

export const initTranslations = async (
  isLocal: string,
  magnoliaHost: string,
  locale?: string,
  translationsVersion?: string
): Promise<void> => {
  await i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(ChainedBackend)
    .init({
      ...getOptionsClient(locale, undefined, isLocal, magnoliaHost, translationsVersion),
      preload: isClient() ? languages : []
    });
};

export const boot = async (
  isDev: boolean,
  magnoliaHost: string,
  mGNLProps?: PageProps,
  currentLanguage?: string,
  isLocal?: string,
  translationVersion?: string
): Promise<YsnConfigModel> => {
  const { config: mGNLConfig, magnoliaContext } = mGNLProps || {};
  await initTranslations(isLocal ?? '', magnoliaHost, currentLanguage, translationVersion);
  initConfig(mGNLConfig);
  const config = configService.get();
  logger.log('booting...');
  initLanguage(currentLanguage);
  initCurrency();
  await initAuth(config);
  if (!magnoliaContext?.isMagnoliaEdit && !magnoliaContext?.isMagnolia) {
    await initTracking(config, isDev);
  }
  await appService.hydrate(config);
  logger.log('App hydrated');
  return config;
};
